import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface SegmentRateBarProps {
    level: number;
    maxLevel?: number;
    title?: string;
    onChange: (level: number) => void;
    disabled?: boolean;
}

/**
 * Compact segment rate bar component like SegmentLevelFilter.
 * Shows selected level number and allows click to select/deselect.
 */
export default function SegmentRateBar({ level, maxLevel, title, onChange, disabled = false }: SegmentRateBarProps) {
    const theme = useTheme();
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const dynamicMaxLevel = maxLevel || levelDefinitions.length;

    const getLevelColor = (levelIndex: number) => {
        return theme.palette.levels?.[levelIndex as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const handleSegmentClick = (clickedLevel: number) => {
        if (disabled) return;
        const newLevel = clickedLevel === level ? 0 : clickedLevel;
        onChange(newLevel);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
            }}
        >
            {/* Level Number Display */}
            <Box
                onClick={() => onChange(0)}
                sx={{
                    minWidth: 32,
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: theme.palette.primary.main,
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: disabled ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    '&:hover': disabled
                        ? {}
                        : {
                              transform: 'scale(1.05)',
                          },
                }}
                title={`${levelDefinitions[level - 1]?.title || `Level ${level}`}: ${levelDefinitions[level - 1]?.description || ''}`}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        color: 'white',
                    }}
                >
                    {level}
                </Typography>
            </Box>

            {/* Compact Level Segments */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {Array.from({ length: dynamicMaxLevel }, (_, i) => i + 1).map((segmentLevel) => (
                    <Box
                        key={segmentLevel}
                        onClick={() => handleSegmentClick(segmentLevel)}
                        sx={{
                            width: 12,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor:
                                segmentLevel <= level
                                    ? getLevelColor(segmentLevel)
                                    : theme.palette.mode === 'dark'
                                      ? theme.palette.grey[700]
                                      : theme.palette.grey[300],
                            cursor: disabled ? 'default' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: disabled ? 0.5 : segmentLevel <= level ? 1 : 0.3,
                            '&:hover': disabled
                                ? {}
                                : {
                                      transform: 'scale(1.1)',
                                      opacity: 1,
                                  },
                        }}
                        title={`${levelDefinitions[segmentLevel - 1]?.title || `Level ${segmentLevel}`}: ${levelDefinitions[segmentLevel - 1]?.description || ''}`}
                    />
                ))}
            </Box>

            {/* Level Name Display */}
            {level > 0 && (
                <Typography
                    variant="caption"
                    sx={{
                        ml: 1,
                        px: 1,
                        py: 0.5,
                        backgroundColor: alpha(getLevelColor(level), 0.1),
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'text.primary',
                        minWidth: 80,
                        textAlign: 'center',
                    }}
                >
                    {levelDefinitions[level - 1]?.title || `Level ${level}`}
                </Typography>
            )}
        </Box>
    );
}
